const SVG_PAINTABLE_ELEMENTS = "path,circle,rect,polygon,polyline,ellipse,line,g,use";
const DEFAULT_PARTNER_BLOCK_SELECTOR = "[data-partners-slider-block]";
const DEFAULT_PARTNER_ICON_SELECTOR =
  "[data-partners-slider-block-default] .partner__icon:not([data-partners-slider-managed])";

const svgRequestCache = new Map();
let partnerBlockIndex = 0;

function runWhenDocumentReady(callback) {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", callback, { once: true });
  } else {
    callback();
  }
}

function applyCurrentColor(svgElement, includeStroke = false) {
  svgElement.querySelectorAll(SVG_PAINTABLE_ELEMENTS).forEach((element) => {
    const fill = (element.getAttribute("fill") || "").toLowerCase();
    const stroke = (element.getAttribute("stroke") || "").toLowerCase();
    const inlineStyle = element.getAttribute("style") || "";

    if (fill !== "none") element.setAttribute("fill", "currentColor");
    if (inlineStyle && /fill\s*:/.test(inlineStyle)) {
      element.setAttribute("style", inlineStyle.replace(/fill\s*:\s*[^;]+/g, "fill: currentColor"));
    }
    if (includeStroke && stroke !== "none") {
      element.setAttribute("stroke", "currentColor");
    }
  });

  if (!svgElement.hasAttribute("fill")) {
    svgElement.setAttribute("fill", "currentColor");
  }
  if (includeStroke && !svgElement.hasAttribute("stroke")) {
    svgElement.setAttribute("stroke", "currentColor");
  }

  return svgElement;
}

function loadSvg(url) {
  if (!svgRequestCache.has(url)) {
    const request = window
      .fetch(url, { cache: "force-cache" })
      .then((response) => {
        if (!response.ok) throw new Error(`SVG request failed with status ${response.status}`);
        return response.text();
      })
      .then((source) => {
        const svgDocument = new window.DOMParser().parseFromString(source, "image/svg+xml");
        const svgElement = svgDocument.querySelector("svg");
        if (!svgElement) throw new Error("SVG response did not contain an svg element");
        return svgElement;
      })
      .catch((error) => {
        svgRequestCache.delete(url);
        throw error;
      });

    svgRequestCache.set(url, request);
  }

  return svgRequestCache.get(url);
}

export async function convertSingleSvg(image, { stroke = false } = {}) {
  if (!image || image.dataset.svgConverted === "true") return;

  const sourceUrl = image.getAttribute("src");
  if (!sourceUrl || !/\.svg(\?|#|$)/i.test(sourceUrl)) return;

  const imageId = image.getAttribute("id");
  const imageClasses = image.getAttribute("class") || "";
  image.dataset.svgConverted = "true";

  try {
    const sourceSvg = await loadSvg(sourceUrl);
    if (!image.isConnected) return;

    const inlineSvg = sourceSvg.cloneNode(true);
    if (imageId) inlineSvg.setAttribute("id", imageId);
    inlineSvg.setAttribute("class", `${imageClasses} replaced-svg`.trim());
    inlineSvg.removeAttribute("xmlns:a");
    image.replaceWith(applyCurrentColor(inlineSvg, stroke));

    if (typeof window.setTransformOrigin === "function") {
      window.setTransformOrigin();
    }
  } catch {
    delete image.dataset.svgConverted;
  }
}

export function convertSvg(selector, options = {}) {
  document.querySelectorAll(selector).forEach((image) => {
    convertSingleSvg(image, options);
  });
}

window.convertSVG = convertSvg;

function getObservationTarget(node, closestSelector) {
  if (!closestSelector || typeof node.closest !== "function") return node;
  return node.closest(closestSelector) || node;
}

function isNearViewport(node, { preloadX = 0, preloadY = 0 } = {}) {
  const bounds = node.getBoundingClientRect();
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

  return (
    bounds.bottom >= -preloadY &&
    bounds.top <= viewportHeight + preloadY &&
    bounds.right >= -preloadX &&
    bounds.left <= viewportWidth + preloadX
  );
}

export function observeInlineSvg(selector, options = {}) {
  const {
    rootMargin = "200px 0px",
    threshold = 0.01,
    observeClosest,
    preloadX = 0,
    preloadY = 0,
    ...conversionOptions
  } = options;
  const images = Array.from(document.querySelectorAll(selector));
  if (!images.length) return;

  if (typeof window.IntersectionObserver !== "function") {
    convertSvg(selector, conversionOptions);
    return;
  }

  const imagesByTarget = new Map();
  images.forEach((image) => {
    const target = getObservationTarget(image, observeClosest);
    if (!imagesByTarget.has(target)) imagesByTarget.set(target, []);
    imagesByTarget.get(target).push(image);
  });

  const observer = new window.IntersectionObserver(
    (entries, instance) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        (imagesByTarget.get(entry.target) || []).forEach((image) => {
          convertSingleSvg(image, conversionOptions);
        });
        instance.unobserve(entry.target);
      });
    },
    { rootMargin, threshold }
  );

  imagesByTarget.forEach((targetImages, target) => {
    if (isNearViewport(target, { preloadX, preloadY })) {
      targetImages.forEach((image) => convertSingleSvg(image, conversionOptions));
    } else {
      observer.observe(target);
    }
  });
}

function createSortablePartnerItems(items) {
  return items.map((item, position) => {
    const name = (item.dataset.name || "").trim().toLowerCase();
    const rawIndex = Number.parseInt(item.dataset.index || "", 10);

    return {
      item,
      name,
      hasName: Boolean(name),
      index: Number.isNaN(rawIndex) ? Number.MAX_SAFE_INTEGER : rawIndex,
      position,
    };
  });
}

function comparePartnerNamesAscending(first, second) {
  if (first.hasName && second.hasName) {
    return (
      first.name.localeCompare(second.name, undefined, {
        numeric: true,
        sensitivity: "base",
      }) || first.position - second.position
    );
  }

  if (first.hasName) return -1;
  if (second.hasName) return 1;
  return first.position - second.position;
}

function sortPartnerItems(items, compare) {
  return createSortablePartnerItems(items)
    .sort(compare)
    .map(({ item }) => item);
}

function buildPartnerRow(items) {
  const row = document.createElement("div");
  row.className = "partners__slider-row";

  for (let copyIndex = 0; copyIndex < 2; copyIndex += 1) {
    const slider = document.createElement("div");
    const list = document.createElement("div");

    slider.className = "partners__slider partners__slider--css w-dyn-list";
    list.setAttribute("role", "list");
    list.className = "content__partners--list-homepage w-dyn-items";

    items.forEach((item) => {
      const clone = item.cloneNode(true);

      clone.querySelectorAll(".partner__icon").forEach((image) => {
        const deferredSource = image.dataset.partnersDeferredSrc;
        if (deferredSource && !image.getAttribute("src")) {
          image.setAttribute("src", deferredSource);
        }
        image.dataset.partnersSliderManaged = "true";
      });

      list.appendChild(clone);
    });

    slider.appendChild(list);
    row.appendChild(slider);
  }

  return row;
}

function activatePartnerRoot(root) {
  if (!root || root.dataset.partnersTickerBuilt === "true") return;

  const host = root.querySelector(".partner__slider--wrap") || root;
  if (host.querySelector(".partners__slider-row")) return;

  const sourceSlider =
    Array.from(host.children).find(
      (child) =>
        child.classList?.contains("partners__slider") && child.classList.contains("w-dyn-list")
    ) || null;
  const sourceList = sourceSlider?.querySelector(".content__partners--list-homepage");
  const partnerItems = sourceList
    ? Array.from(sourceList.children).filter((item) =>
        item.classList?.contains("partners__list-item--wrap")
      )
    : [];
  if (!sourceSlider || !partnerItems.length) return;

  const rows = document.createElement("div");
  const observationId =
    root.dataset.partnersSliderObserve || `partners-slider-${(partnerBlockIndex += 1)}`;

  rows.className = "partners__list--wrap-vertical";
  rows.appendChild(buildPartnerRow(sortPartnerItems(partnerItems, comparePartnerNamesAscending)));
  rows.appendChild(
    buildPartnerRow(
      sortPartnerItems(
        partnerItems,
        (first, second) => first.index - second.index || comparePartnerNamesAscending(first, second)
      )
    )
  );
  rows.appendChild(
    buildPartnerRow(
      sortPartnerItems(partnerItems, (first, second) => comparePartnerNamesAscending(second, first))
    )
  );

  root.dataset.partnersSliderObserve = observationId;
  sourceSlider.replaceWith(rows);
  root.dataset.partnersTickerBuilt = "true";

  observeInlineSvg(`[data-partners-slider-observe="${observationId}"] .partner__icon`, {
    observeClosest: ".partners__list-item--wrap",
    rootMargin: "200px 200px",
    preloadX: 200,
    preloadY: 200,
  });
}

export function initPartnerTickerRows(selector = DEFAULT_PARTNER_BLOCK_SELECTOR) {
  document.querySelectorAll(selector).forEach((root) => {
    if (root.dataset.partnersTickerBuilt === "true") return;

    root.querySelectorAll(".partner__slider--wrap .partner__icon").forEach((image) => {
      const source = image.getAttribute("src");
      image.dataset.partnersSliderManaged = "true";

      if (!source || image.dataset.partnersDeferredSrc) return;
      image.dataset.partnersDeferredSrc = source;
      image.removeAttribute("src");
    });

    if (
      typeof window.IntersectionObserver !== "function" ||
      isNearViewport(root, { preloadY: 400 })
    ) {
      activatePartnerRoot(root);
      return;
    }

    const observer = new window.IntersectionObserver(
      (entries, instance) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          activatePartnerRoot(entry.target);
          instance.unobserve(entry.target);
        });
      },
      { rootMargin: "400px 0px", threshold: 0.01 }
    );

    observer.observe(root);
  });
}

export function initPartners() {
  initPartnerTickerRows();
  if (document.querySelector(DEFAULT_PARTNER_ICON_SELECTOR)) {
    observeInlineSvg(DEFAULT_PARTNER_ICON_SELECTOR, {
      observeClosest: ".partners__list-item--wrap",
      rootMargin: "200px 200px",
      preloadX: 200,
      preloadY: 200,
    });
  }
}

runWhenDocumentReady(initPartners);
